'use client'

import { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react'
import { useDemo } from '../demo-context'
import { COACH_MARKS, type CoachMark } from './coach-mark-data'

/** Fisher-Yates shuffle (returns a new array) */
function shuffle<T>(arr: T[]): T[] {
  const result = [...arr]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

function buildOrderedMarks(): CoachMark[] {
  const immediate = COACH_MARKS.filter(m => m.triggerAfter === 'immediate')
  const rest = shuffle(COACH_MARKS.filter(m => m.triggerAfter !== 'immediate'))
  return [...immediate, ...rest]
}

// ─── Delay durations ────────────────────────────────────────────────

const DELAY_INITIAL = 1500     // before the very first mark
const DELAY_NEXT = 1000        // user clicked the action button
const DELAY_SKIP = 3000        // user clicked "Skip"
const DELAY_AUTO_DISMISS = 800 // mark was auto-dismissed by state change

// ─── Shared context ────────────────────────────────────────────────

interface CoachMarksContextValue {
  activeCoachMark: CoachMark | null
  hasNext: boolean
  dismiss: (id: string) => void
  next: (id: string) => void
  triggerEvent: (eventName: string) => void
}

const CoachMarksContext = createContext<CoachMarksContextValue | null>(null)

export function CoachMarksStateProvider({ children }: { children: React.ReactNode }) {
  const { state } = useDemo()

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [triggeredEvents, setTriggeredEvents] = useState<Set<string>>(() => new Set(['immediate']))
  const [activeCoachMark, setActiveCoachMark] = useState<CoachMark | null>(null)
  const [isDelaying, setIsDelaying] = useState(true)
  const [delayMs, setDelayMs] = useState(DELAY_INITIAL)

  const [orderedMarks] = useState<CoachMark[]>(() => buildOrderedMarks())
  const firstSongTriggeredRef = useRef(false)

  // ─── Helper to dismiss + set delay ────────────────────────────────

  const dismissWithDelay = useCallback((id: string, delay: number) => {
    setDismissedIds(prev => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
    setActiveCoachMark(null)
    setDelayMs(delay)
    setIsDelaying(true)
  }, [])

  // ─── Auto-trigger events based on demo state ─────────────────────

  /* eslint-disable react-hooks/set-state-in-effect -- syncing demo phase changes to coach mark triggers */
  useEffect(() => {
    if (state.phase === 'live') {
      setTriggeredEvents(prev => {
        if (prev.has('go_live')) return prev
        const next = new Set(prev)
        next.add('go_live')
        return next
      })
    }
  }, [state.phase])

  useEffect(() => {
    if (state.nowPlaying && !firstSongTriggeredRef.current) {
      firstSongTriggeredRef.current = true
      setTriggeredEvents(prev => {
        if (prev.has('first_song')) return prev
        const next = new Set(prev)
        next.add('first_song')
        return next
      })
    }
  }, [state.nowPlaying])

  // ─── Auto-dismiss when satisfiedWhen condition is met ─────────────

  useEffect(() => {
    if (!activeCoachMark) return
    // Dismiss if the action was completed
    if (activeCoachMark.satisfiedWhen?.(state)) {
      dismissWithDelay(activeCoachMark.id, DELAY_AUTO_DISMISS)
      return
    }
    // Dismiss if the feature was disabled while the coach was showing
    if (activeCoachMark.enabledWhen && !activeCoachMark.enabledWhen(state)) {
      dismissWithDelay(activeCoachMark.id, DELAY_AUTO_DISMISS)
    }
  }, [state, activeCoachMark, dismissWithDelay])

  /* eslint-enable react-hooks/set-state-in-effect */

  // ─── Manual trigger ───────────────────────────────────────────────

  const triggerEvent = useCallback((eventName: string) => {
    setTriggeredEvents(prev => {
      if (prev.has(eventName)) return prev
      const next = new Set(prev)
      next.add(eventName)
      return next
    })
  }, [])

  // ─── Dismiss (Skip — longer delay) ───────────────────────────────

  const dismiss = useCallback((id: string) => {
    dismissWithDelay(id, DELAY_SKIP)
  }, [dismissWithDelay])

  // ─── Next (action button — shorter delay) ─────────────────────────

  const next = useCallback((id: string) => {
    dismissWithDelay(id, DELAY_NEXT)
  }, [dismissWithDelay])

  // ─── Pick next eligible mark ──────────────────────────────────────

  useEffect(() => {
    if (activeCoachMark || isDelaying) return

    // Collect marks that are already satisfied so we can dismiss them
    // (this unblocks prerequisite chains)
    const newlyDismissed: string[] = []

    const eligible = orderedMarks.find(mark => {
      if (dismissedIds.has(mark.id)) return false
      if (!triggeredEvents.has(mark.triggerAfter)) return false
      if (mark.prerequisite && !dismissedIds.has(mark.prerequisite) && !newlyDismissed.includes(mark.prerequisite)) return false
      // Skip marks whose condition is already satisfied — treat as dismissed
      if (mark.satisfiedWhen?.(state)) {
        newlyDismissed.push(mark.id)
        return false
      }
      // Skip marks whose feature is currently disabled (don't dismiss — may re-enable later)
      if (mark.enabledWhen && !mark.enabledWhen(state)) return false
      return true
    })

    // Record skipped-as-satisfied marks so prerequisite chains work
    if (newlyDismissed.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronously dismiss already-satisfied marks to unblock prerequisites
      setDismissedIds(prev => {
        const next = new Set(prev)
        for (const id of newlyDismissed) next.add(id)
        return next
      })
    }

    if (eligible) {
      setActiveCoachMark(eligible)
    }
  }, [activeCoachMark, dismissedIds, triggeredEvents, isDelaying, state, orderedMarks])

  // ─── Delay timer between marks ────────────────────────────────────

  useEffect(() => {
    if (!isDelaying) return
    const timer = setTimeout(() => {
      setIsDelaying(false)
    }, delayMs)
    return () => clearTimeout(timer)
  }, [isDelaying, delayMs])

  // ─── Check if there's a next mark after the current one ──────────

  const hasNext = (() => {
    if (!activeCoachMark) return false
    const hypotheticalDismissed = new Set([...dismissedIds, activeCoachMark.id])
    return orderedMarks.some(mark => {
      if (hypotheticalDismissed.has(mark.id)) return false
      if (!triggeredEvents.has(mark.triggerAfter)) return false
      if (mark.prerequisite && !hypotheticalDismissed.has(mark.prerequisite)) return false
      if (mark.satisfiedWhen?.(state)) return false
      if (mark.enabledWhen && !mark.enabledWhen(state)) return false
      return true
    })
  })()

  const value: CoachMarksContextValue = { activeCoachMark, hasNext, dismiss, next, triggerEvent }

  return (
    <CoachMarksContext.Provider value={value}>
      {children}
    </CoachMarksContext.Provider>
  )
}

export function useCoachMarks(): CoachMarksContextValue {
  const ctx = useContext(CoachMarksContext)
  if (!ctx) {
    throw new Error('useCoachMarks must be used within a CoachMarksStateProvider')
  }
  return ctx
}
